import type { ComboboxOption } from '@klear/constants'
import { CURRENCIES } from '@klear/constants/currency-iso4217'
import isString from 'lodash/isString'
import fromPairs from 'lodash/fromPairs'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

export const CURRENCY_CODES = uniq(CURRENCIES.map((currency) => currency.currencyCode)).sort()

export type CurrencyCode = (typeof CURRENCY_CODES)[number]

export const DEFAULT_CURRENCY_CODE = 'USD' as const

export const CURRENCY_CODE_OPTIONS: ComboboxOption<CurrencyCode>[] = uniqBy(
  CURRENCIES.map(
    (currency): ComboboxOption<CurrencyCode> => ({
      value: currency.currencyCode,
      label: `${currency.currencyCode} - ${currency.currencyName}`
    })
  ),
  'value'
)
const CURRENCY_CODES_SET = new Set(CURRENCY_CODES)
const currencyMinorDigitsEntries = CURRENCIES.map((currency): [CurrencyCode, number] => [
  currency.currencyCode,
  currency.currencyMinorUnits
])

const CURRENCY_MINOR_DIGITS_BY_CODE: Partial<Record<CurrencyCode, number>> = fromPairs(
  currencyMinorDigitsEntries
) as Partial<Record<CurrencyCode, number>>

export const normalizeCurrencyCode = (currency: unknown): CurrencyCode | null => {
  if (!isString(currency)) {
    return null
  }
  const normalized = currency.trim().toUpperCase()
  return CURRENCY_CODES_SET.has(normalized as CurrencyCode) ? (normalized as CurrencyCode) : null
}

export const getCurrencyMinorDigits = (currency: string | CurrencyCode | null | undefined): number => {
  const normalizedCurrency = normalizeCurrencyCode(currency)
  if (!normalizedCurrency) {
    return 2
  }
  return CURRENCY_MINOR_DIGITS_BY_CODE[normalizedCurrency] ?? 2
}

export const getCurrencyMultiplier = (currency: string | CurrencyCode | null | undefined): number => {
  return 10 ** getCurrencyMinorDigits(currency)
}
